import { MINUTE_HEIGHT_PX } from 'constants/hour';
import React from 'react';
import debug from 'utils/debug';
import { roundToNearest, roundUpToFactorOf } from 'utils/numbers';
import type { ClickDragReturn } from './ClickDragAdjust';
import styles from './ClickDragAdjust.module.scss';

const IGNORE_ACTION_THRESHOLD_PX = 1;

interface Props {
  /** #id or .classname of draggable container */
  parentSelector: string;
  parentRef: {
    current: HTMLElement | null;
  };
  pixelsPerMinute: number;
  offsetTopPx: number;
  offsetLeftPx: number;
  selectionWidthPx: number | null;
  onChange: (clickDragReturn: ClickDragReturn) => void;
  snapToMinutes: number;
}

interface State {
  selectionFromY: number | null;
  selectionToY: number | null;
  isSelecting: boolean;
  containerWidth: number | null;
}

export class ClickDrag extends React.Component<Props, State> {
  static defaultProps = {
    pixelsPerMinute: MINUTE_HEIGHT_PX,
    selectionFromY: 0,
    selectionToY: 0,
    isSelecting: false,
  };

  state: State = {
    selectionFromY: 0,
    selectionToY: 0,
    isSelecting: false,
    containerWidth: null,
  };

  // We're avoiding attaching these events directly to the ref, because
  // the refs are null when this component unmounts for some reason.
  // This adds some complexity (see props.parentSelector usage),
  // but avoids memory leaks, so yey!
  componentDidMount() {
    document.addEventListener('dragstart', this.handleMouseDown);
  }

  componentWillUnmount() {
    document.removeEventListener('dragstart', this.handleMouseDown);
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('mousemove', this.handleMouseMove);
  }

  handleMouseUp = (event: MouseEvent) => {
    // mouseUp outside of a "onClickOutside" parent should not trigger a clickoutside
    event.stopPropagation();

    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('mousemove', this.handleMouseMove);

    const elem = this.props.parentRef.current;
    const { selectionFromY } = this.state;
    if (elem && selectionFromY) {
      const clientRect = elem.getBoundingClientRect();
      const selectionToY = roundUpToFactorOf(
        event.clientY - clientRect.top - this.props.offsetTopPx,
        this.props.snapToMinutes * this.props.pixelsPerMinute
      );
      const height = selectionToY - selectionFromY;
      const isIgnored = height < IGNORE_ACTION_THRESHOLD_PX;

      if (!isIgnored) {
        this.props.onChange({
          selectionFromY,
          selectionToY,
        });
      }

      this.setState({
        selectionFromY: null,
        selectionToY: null,
        isSelecting: false,
      });
    } else {
      debug.log('aborted mouseUp', elem, selectionFromY);
    }
  };

  handleMouseMove = (event: MouseEvent) => {
    const elem = this.props.parentRef.current;
    // In case we've stopped the selection, but this handleMouseMove call was throttle'd
    // and triggered AFTER handleHouseUp, don't set new value:

    if (elem && this.state.isSelecting) {
      const { top } = elem.getBoundingClientRect();
      const selectionToY = roundUpToFactorOf(
        event.clientY - top - this.props.offsetTopPx,
        this.props.snapToMinutes * this.props.pixelsPerMinute
      );
      this.setState({ selectionToY });
    }
  };

  handleMouseDown = (event: MouseEvent) => {
    // We do this instead of binding directly to parentRef.current because we can't reliably
    // unbind the eventListener when entire view gets torn down.
    const element = event.target as HTMLElement;
    if (!element?.matches?.(this.props.parentSelector)) {
      return;
    }
    event.preventDefault();

    document.addEventListener('mouseup', this.handleMouseUp);
    document.addEventListener('mousemove', this.handleMouseMove);

    const clientRect = element.getBoundingClientRect();
    const containerWidth = element.scrollWidth;

    const fromY = event.clientY - clientRect.top - this.props.offsetTopPx;
    const snapTo = this.props.snapToMinutes * this.props.pixelsPerMinute;
    const selectionFromY = roundToNearest(fromY, snapTo);

    this.setState({
      selectionFromY,
      selectionToY: selectionFromY,
      isSelecting: true,
      containerWidth,
    });
  };

  render() {
    const { selectionFromY, selectionToY, containerWidth } = this.state;

    if (!selectionFromY || !selectionToY) {
      return null;
    }

    const height = selectionToY - selectionFromY;
    const isIgnored = height < IGNORE_ACTION_THRESHOLD_PX;

    if (isIgnored) {
      return null;
    }

    // If selection width isn't specified, fill the container
    const fallbackWidth = containerWidth ? containerWidth - 5 : 0;
    const width = this.props.selectionWidthPx || fallbackWidth;

    return (
      <div
        className={styles.clickDragContainer}
        style={{
          height,
          left: this.props.offsetLeftPx,
          top: selectionFromY + this.props.offsetTopPx,
          width,
        }}
      />
    );
  }
}
